import React, { useState } from "react"
import { Link } from "react-router-dom"
import { useNavigateToPost } from "../../hooks/useNavigateToPost"
import { SmartDateFormat } from "../../util/dates"
import { PlayCircle } from "react-bootstrap-icons"
import FreeTextFormat from "../shared/FreeTextFormat"
import CommentSectionPreview from "../comments/CommentSectionPreview"
import { ShareUrlButton } from "../ShareUrlButton"
import { ShowHideButton } from "../shared/Button"
import { ReactionBlock } from "./ReactionBlock"
import RelatedPosts from "../RelatedPosts"
import type { VideoDisplayConfig } from "../../types/videoDisplay"

const styles = {
  header: "text-base md:text-2xl font-bold text-black-800 leading-none md:leading-tight",
  bandName: "text-gray-500 leading-none text-xs",
  postDate: "text-xs md:text-sm text-black-800 leading-none md:leading-normal",
  externalLink: "text-xs md:text-sm text-gray-500 italic",
  description: "text-xs md:text-sm text-gray-500 mt-1",
}

interface VideoListItemShortProps {
  video: any
  adminMode?: boolean
  updateStatus?: () => void
  config?: VideoDisplayConfig
}

export const VideoListItemShort = ({
  video,
  adminMode = false,
  updateStatus = () => {},
  config = { showComments: "full", showRelatedPosts: "full" },
}: VideoListItemShortProps) => {
  const { showComments, showRelatedPosts } = config
  const [postStatus, setPostStatus] = useState(video.status)
  const navigateToPostScreen = useNavigateToPost(video.nada_path)

  return (
    <div>
      <div className="flex gap-3 items-start">
        <div className="w-1/3 flex-shrink-0">
          <div className="relative aspect-square w-full cursor-pointer" onClick={navigateToPostScreen}>
            <img
              src={video.external_photo_src}
              alt={video.title_cleaned || video.title}
              className="aspect-square object-cover w-full"
            />
            <PlayCircle
              className="absolute inset-0 m-auto"
              style={{ color: "white", width: "2rem", height: "2rem" }}
            />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-1">
            <h2
              className={styles.header + " cursor-pointer flex-1 min-w-0"}
              onClick={navigateToPostScreen}
            >
              <FreeTextFormat
                text={
                  video.title
                    ? video.title_cleaned || video.title
                    : `New Headline from ${video.band_name}`
                }
                expandable={false}
                expanded={false}
                linkify={true}
              />
            </h2>
            <ShareUrlButton nadaUrl={video.nada_url} renderMode="icon" alignment="align-middle" />
          </div>

          <Link className={styles.bandName} to={video.local_path}>
            {video.band_name}
          </Link>

          <div className={styles.postDate}>
            Released:&nbsp;
            {SmartDateFormat({ dateStr: video.published_at, includeTime: false })}
          </div>

          {video.view_on && (
            <div className={styles.externalLink}>
              <a href={video.external_url} target="_blank" rel="noreferrer">
                view on&nbsp;{video.view_on}
              </a>
            </div>
          )}
        </div>
      </div>

      <div className="mt-2">
        <div className={styles.description}>
          <FreeTextFormat text={video.description} expandable={false} />
        </div>

        <div className="mt-4">
          <RelatedPosts postId={video.id} nadaUrl={video.nada_url} mode={showRelatedPosts} />
        </div>
        <div className="mt-6">
          <ReactionBlock
            contentType="Post"
            contentId={video.id}
            postId={video.id}
          />
        </div>
        <CommentSectionPreview postId={video.id} postPath={video.nada_url} mode={showComments} />
        {adminMode && (
          <ShowHideButton
            updateStatus={updateStatus}
            postStatus={postStatus}
            setPostStatus={setPostStatus}
          />
        )}
      </div>
    </div>
  )
}
